import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type PageContentMap = Record<string, string>;

const CACHE_PREFIX = "page_content_cache_v1:";

function readCache(page: string): PageContentMap | undefined {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + page);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed as PageContentMap;
  } catch {
    /* ignore */
  }
  return undefined;
}

function writeCache(page: string, map: PageContentMap) {
  try {
    localStorage.setItem(CACHE_PREFIX + page, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

export function usePageContent(page: string, fallback: PageContentMap) {
  const cached = readCache(page);

  const { data } = useQuery({
    queryKey: ["page_content", page],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("page_content")
        .select("section,value")
        .eq("page", page);
      if (error) throw error;
      const map: PageContentMap = {};
      data?.forEach((r) => {
        map[r.section] = r.value;
      });
      writeCache(page, map);
      return map;
    },
    staleTime: 30_000,
    initialData: cached,
  });

  // Priority: fresh DB data > cached DB data > fallback. This prevents the
  // "flash of old/fallback content" when revisiting a page after edits.
  const source = data ?? cached;
  const t = (key: string) => source?.[key] ?? fallback[key] ?? "";
  return { t, raw: source ?? fallback };
}

/**
 * Render text with simple **bold** markdown support.
 * Returns array of React-friendly fragments.
 */
export function renderInline(text: string): (string | { bold: string })[] {
  const parts: (string | { bold: string })[] = [];
  const regex = /\*\*(.+?)\*\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push({ bold: m[1] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}
